import { derived, get, type Writable, writable } from "svelte/store";
import type { HistoryItem } from "../types/global";
import { SqrlStorage } from "../util/SqrlStorage";
import { STORAGE } from "../constants/storage";
import { SqrlConsentBanner } from "../util/SqrlConsentBanner";
import * as protobufHistory from "@otto-ec/squirrel_protobuf-history";

class HistoryStore {
  private readonly maxTotalLength: number;
  private readonly _store: Writable<HistoryItem[]>;

  constructor(maxTotalLength: number = 10) {
    this.maxTotalLength = maxTotalLength;
    this._store = writable(this.maybeLoadInitial(), (set) => {
      if (typeof window !== "undefined" && typeof document !== "undefined") {
        const handleStorage = (event: StorageEvent) => {
          if (event.key === STORAGE.HISTORICAL_DATA) set(this.maybeLoadInitial());
        };

        window.addEventListener("storage", handleStorage);

        return () => window.removeEventListener("storage", handleStorage);
      }
    });
  }

  private removeDuplicates(searches: HistoryItem[]): HistoryItem[] {
    const seen = new Set();
    return searches
      .slice()
      .reverse()
      .filter((item) => {
        const duplicate = seen.has(item.term);
        seen.add(item.term);
        return !duplicate;
      })
      .reverse();
  }

  private writeToStorage(searches: HistoryItem[]) {
    const searchString: string = JSON.stringify(searches);
    SqrlStorage.setItem(STORAGE.HISTORICAL_DATA, searchString);
  }

  private maybeLoadInitial() {
    const searchesString: string | null = SqrlStorage.getItem(STORAGE.HISTORICAL_DATA);
    if (!searchesString) {
      return [];
    }
    const historySearchItems: HistoryItem[] = JSON.parse(searchesString);
    historySearchItems.sort((a, b) => b.date - a.date);
    return this.removeDuplicates(historySearchItems);
  }

  private deletePositions(terms: string[], last: HistoryItem[]) {
    return last.filter((item) => !terms.includes(item.term));
  }

  private handleDeleteHistory() {
    return (last: HistoryItem[]) => {
      return !last.some((historyItem) => historyItem.selected)
        ? []
        : last.filter((historyItem) => !historyItem.selected);
    };
  }

  private isItemInHistory(term: string, last: HistoryItem[]) {
    return last.some((search: HistoryItem): boolean => search.term === term);
  }

  private add(term: string): (last: HistoryItem[]) => HistoryItem[] {
    return (last: HistoryItem[]) => {
      if (!term) {
        return [];
      }
      const newSearch: HistoryItem = {
        term: term.toLowerCase(),
        date: new Date().getTime()
      };

      let searches: HistoryItem[] = last;

      if (this.isItemInHistory(term, last)) {
        searches = this.deletePositions([term], searches);
      }

      searches.unshift(newSearch);
      if (searches.length > this.maxTotalLength) {
        searches.pop();
      }
      return searches;
    };
  }

  set(value: HistoryItem[]) {
    this._store.set(value);
    this.writeToStorage(value);
  }

  subscribe() {
    return this._store.subscribe;
  }

  update(callback: (value: HistoryItem[]) => HistoryItem[]) {
    return this._store.update((last) => {
      if (SqrlConsentBanner.isEnabled()) {
        const value = callback(last);
        this.writeToStorage(value);
        return value;
      } else {
        return last;
      }
    });
  }

  addTerm(term: string) {
    this.update(this.add(term));
  }

  deleteHistory() {
    this.update(this.handleDeleteHistory());
    if (get(this.store).length === 0) {
      const inputElement: HTMLInputElement | null = document.querySelector(
        ".js_squirrel_searchbar__input"
      );
      inputElement?.focus();
    }
  }

  select(index: number) {
    this.update((last) => {
      last[index].selected = !last[index].selected;
      return last;
    });
  }

  resetSelected() {
    this.update((last) => {
      last.forEach((item) => (item.selected = undefined));
      return last;
    });
  }
  reInitialize() {
    this.set(this.maybeLoadInitial());
  }

  get store() {
    return this._store;
  }
}

export const submitHistory = new HistoryStore(10);
export const visualSearches = derived(submitHistory.store, ($submitHistory) => {
  return $submitHistory.slice(0, 5);
});
export const isEmpty = derived(submitHistory.store, ($submitHistory) => {
  return $submitHistory.length === 0;
});
export const someSelected = derived(submitHistory.store, ($submitHistory) => {
  return $submitHistory.some((historyItem) => historyItem.selected);
});

function createHistoryMode() {
  const store = writable(true);
  const { subscribe, set, update } = store;
  return {
    subscribe,
    set,
    update,
    toggle() {
      update((value) => {
        value = !value;
        if (value) {
          submitHistory.resetSelected();
        }
        return value;
      });
    }
  };
}

export const historyViewMode = createHistoryMode();

export const encodedHistory: () => string | undefined = () => {
  try {
    if (SqrlConsentBanner.isEnabled()) {
      const history = protobufHistory.createHistory(
        get(submitHistory.store) as protobufHistory.protoHistory.History.HistoryItem[],
        Date.now()
      );

      const binary: Uint8Array = protobufHistory.protoHistory.History.encode(history).finish();
      return btoa(String.fromCodePoint(...binary));
    } else {
      return undefined;
    }
  } catch (e) {
    return undefined;
  }
};
